import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { getMutationQueue, removeMutation, incrementMutationRetry } from './persister'
import { api } from '@/api/client'
import { useConnectionHealthStore } from '@/stores/connectionHealthStore'

/**
 * Hook to track online/offline status.
 *
 * Browser-level signal only (same semantics as the old
 * navigator.onLine listener). The connectionHealthStore owns the
 * online/offline event listeners and the react-query onlineManager sync
 * (see initConnectionHealth in main.tsx); this is a pure reader.
 */
export function useOnlineStatus(): boolean {
  return useConnectionHealthStore((s) => s.browserOnline)
}

/**
 * Hook to process queued mutations when coming back online
 */
export function useOfflineSync(): {
  isProcessing: boolean
  pendingCount: number
  processQueue: () => Promise<void>
} {
  const [isProcessing, setIsProcessing] = useState(false)
  const [pendingCount, setPendingCount] = useState(0)
  const queryClient = useQueryClient()
  const isOnline = useOnlineStatus()

  // Check pending count on mount
  useEffect(() => {
    const checkPending = async () => {
      const queue = await getMutationQueue()
      setPendingCount(queue.length)
    }
    checkPending()
  }, [])

  // Auto-process queue when coming online
  useEffect(() => {
    if (isOnline && pendingCount > 0 && !isProcessing) {
      processQueue()
    }
  }, [isOnline, pendingCount])

  const processQueue = async () => {
    const queue = await getMutationQueue()
    if (queue.length === 0) return

    setIsProcessing(true)
    
    for (const mutation of queue) {
      try {
        // Process based on method
        switch (mutation.method) {
          case 'POST':
            await api.post(mutation.endpoint, mutation.data as Record<string, unknown>)
            break
          case 'PUT':
            await api.put(mutation.endpoint, mutation.data as Record<string, unknown>)
            break
          case 'PATCH':
            await api.patch(mutation.endpoint, mutation.data as Record<string, unknown>)
            break
          case 'DELETE':
            await api.delete(mutation.endpoint)
            break
        }
        
        // Success - remove from queue
        await removeMutation(mutation.id)
        setPendingCount(prev => Math.max(0, prev - 1))
        
      } catch (error) {
        // Increment retry count
        await incrementMutationRetry(mutation.id)
        
        // If max retries reached, remove it
        if (mutation.retryCount >= 3) {
          await removeMutation(mutation.id)
          setPendingCount(prev => Math.max(0, prev - 1))
          console.error(`Mutation failed after 3 retries:`, mutation, error)
        }
      }
    }
    
    // Invalidate relevant queries after sync
    queryClient.invalidateQueries({ queryKey: ['receipts'] })
    queryClient.invalidateQueries({ queryKey: ['customers'] })
    
    setIsProcessing(false)
  }

  return { isProcessing, pendingCount, processQueue }
}

/**
 * Connection status indicator.
 *
 * Priority: red offline > amber syncing > amber slow-connection.
 * `offline` now means "no evidence the backend is reachable" — the
 * browser interface being down OR consecutive network-level API
 * failures (connected-to-wifi-but-no-internet, dead uplink, captive
 * portal). `degraded` is the passive latency verdict (Chromium rtt /
 * API duration EWMA) — see connectionHealthStore for signals and
 * thresholds.
 */
export function OfflineIndicator() {
  const status = useConnectionHealthStore((s) => s.status)
  const { pendingCount, isProcessing } = useOfflineSync()

  if (status === 'offline') {
    return (
      <div className="fixed bottom-4 left-4 px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-2 bg-red-500 text-white text-sm font-medium">
        <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
        Offline Mode
      </div>
    )
  }

  if (pendingCount > 0) {
    return (
      <div className="fixed bottom-4 left-4 px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-2 bg-amber-500 text-white text-sm font-medium">
        {isProcessing ? (
          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <span className="w-2 h-2 bg-white rounded-full" />
        )}
        Syncing {pendingCount} pending changes...
      </div>
    )
  }

  if (status === 'degraded') {
    return (
      <div className="fixed bottom-4 left-4 px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-2 bg-amber-500 text-white text-sm font-medium">
        <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
        Slow Connection
      </div>
    )
  }

  return null
}
